import React, { useEffect, useState } from 'react';
import { Compass, Loader2, Plus, AlertTriangle, Users, CheckCircle2 } from 'lucide-react';
import PoPShell from './PoPShell';
import { usePoPAuth } from '../../context/PoPAuthContext';
import { popListGaps, popFlagGap, popExpressGapInterest } from '../../lib/popApi';

const URGENCY_META = {
  low:    { color: 'text-emerald-300', bg: 'bg-emerald-500/15', border: 'border-emerald-500/30', label: 'Low' },
  medium: { color: 'text-amber-300',   bg: 'bg-amber-500/15',   border: 'border-amber-500/30',   label: 'Medium' },
  high:   { color: 'text-rose-300',    bg: 'bg-rose-500/15',    border: 'border-rose-500/30',    label: 'High' },
};

const FlagForm = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    course: '', topic: '', description: '', needed_from_industry: '', urgency: 'medium', sectors_csv: '',
  });
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const payload = { ...f, sectors: f.sectors_csv.split(',').map((s) => s.trim()).filter(Boolean) };
      delete payload.sectors_csv;
      await popFlagGap(payload);
      setOpen(false);
      setF({ course: '', topic: '', description: '', needed_from_industry: '', urgency: 'medium', sectors_csv: '' });
      onCreated();
    } catch (e) { setErr((e && e.response && e.response.data && e.response.data.detail) || 'Could not flag'); }
    finally { setBusy(false); }
  };
  return (
    <div className="mb-6">
      {!open ? (
        <button onClick={() => setOpen(true)} data-testid="gap-flag-open"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-amber-300 hover:bg-amber-200 text-slate-900 text-sm font-semibold">
          <Plus className="w-4 h-4" /> Flag a curriculum gap
        </button>
      ) : (
        <div className="rounded-xl border border-white/10 bg-slate-900/60 p-5 grid sm:grid-cols-2 gap-3">
          <input placeholder="Course (e.g. Enterprise Architecture · MBA-II)" value={f.course} onChange={upd('course')}
            data-testid="gap-form-course"
            className="col-span-2 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          <input placeholder="Topic (e.g. Cloud FinOps in regulated sectors)" value={f.topic} onChange={upd('topic')}
            data-testid="gap-form-topic"
            className="col-span-2 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          <textarea placeholder="Describe the gap — what learners currently miss and why it matters." value={f.description} onChange={upd('description')}
            data-testid="gap-form-desc" rows={3}
            className="col-span-2 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          <textarea placeholder="What we need from Industry — case walkthrough, mentorship, project, live session?" value={f.needed_from_industry} onChange={upd('needed_from_industry')}
            rows={2}
            className="col-span-2 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          <select value={f.urgency} onChange={upd('urgency')} data-testid="gap-form-urgency"
            className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white">
            {Object.entries(URGENCY_META).map(([id, m]) => (<option key={id} value={id} className="bg-slate-900">Urgency: {m.label}</option>))}
          </select>
          <input placeholder="Sectors (comma-separated)" value={f.sectors_csv} onChange={upd('sectors_csv')}
            className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          {err && <div className="text-xs text-rose-300 col-span-2">{err}</div>}
          <div className="col-span-2 flex gap-2">
            <button onClick={submit} disabled={busy || !f.course.trim() || !f.topic.trim() || !f.description.trim()}
              data-testid="gap-form-submit"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-amber-300 hover:bg-amber-200 disabled:opacity-50 text-slate-900 text-sm font-semibold">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Publish gap
            </button>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-md bg-white/5 hover:bg-white/10 text-sm text-white/70">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

const GapCard = ({ g, myUserId, onInterest }) => {
  const [busy, setBusy] = useState(false);
  const alreadyInterested = g.interested_experts?.includes(myUserId);
  const meta = URGENCY_META[g.urgency] || URGENCY_META.medium;
  const doInterest = async () => {
    setBusy(true);
    try { await onInterest(g.id); } catch (_e) { /* noop */ }
    setBusy(false);
  };
  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/40 p-5" data-testid={`gap-card-${g.id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wider text-amber-300 font-bold">Curriculum Gap</span>
        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${meta.bg} ${meta.color}`}>{meta.label} Urgency</span>
      </div>
      <div className="text-base font-serif font-semibold text-white leading-tight">{g.topic}</div>
      <div className="text-[11px] text-white/60 mt-1 mb-3">{g.course} · flagged by {g.flagged_by_name}{g.flagged_by_institution ? ` · ${g.flagged_by_institution}` : ''}</div>
      <p className="text-xs text-white/75 leading-relaxed line-clamp-4 mb-2">{g.description}</p>
      {g.needed_from_industry && (
        <div className="mt-2 rounded-md border border-amber-300/20 bg-amber-300/5 px-3 py-2">
          <div className="text-[9px] tracking-[0.25em] font-bold text-amber-300 mb-0.5">NEEDED FROM INDUSTRY</div>
          <div className="text-[11px] text-white/85">{g.needed_from_industry}</div>
        </div>
      )}
      {g.sectors?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-3">
          {g.sectors.slice(0, 5).map((s) => (
            <span key={s} className="text-[9px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/70">{s}</span>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5">
        <span className="text-[10px] text-white/50 inline-flex items-center gap-1">
          <Users className="w-3 h-3" /> {g.interested_experts?.length || 0} interested
        </span>
        <button onClick={doInterest} disabled={busy || alreadyInterested}
          data-testid={`gap-interest-${g.id}`}
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-semibold ${
            alreadyInterested
              ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 cursor-default'
              : 'bg-amber-300 hover:bg-amber-200 text-slate-900'
          }`}>
          {alreadyInterested ? <><CheckCircle2 className="w-3 h-3" /> Interested</> : 'I can help'}
        </button>
      </div>
    </div>
  );
};

const PoPGaps = () => {
  const { user } = usePoPAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const load = async () => {
    setBusy(true);
    try { const r = await popListGaps(); setItems(r.results || []); }
    catch (_e) { /* noop */ } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);
  const doInterest = async (id) => { await popExpressGapInterest(id); load(); };

  const canFlag = user && ['faculty', 'university', 'super_admin'].includes(user.role);

  return (
    <PoPShell activeTab="gaps">
      <div className="mb-6">
        <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-2">CURRICULUM GAP BOARD</div>
        <h1 className="font-serif text-3xl sm:text-4xl">Bridging what academia can&apos;t teach alone.</h1>
        <p className="text-sm text-white/60 mt-1 max-w-2xl">Faculty flag industry–academia gaps. Industry Professors of Practice step up to close them — case walkthroughs, live sessions, or joint course design.</p>
      </div>

      {canFlag && <FlagForm onCreated={load} />}

      {busy ? (
        <div className="flex items-center justify-center py-16 text-white/60"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/15 bg-slate-900/40 p-10 text-center">
          <AlertTriangle className="w-8 h-8 text-white/30 mx-auto mb-2" />
          <div className="text-sm text-white/60">No gaps flagged yet.</div>
          {canFlag && <div className="text-[11px] text-white/40 mt-1">Be the first faculty to flag one.</div>}
          {!canFlag && <div className="text-[11px] text-white/40 mt-1">Faculty and universities can flag gaps.</div>}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((g) => <GapCard key={g.id} g={g} myUserId={user?.user_id} onInterest={doInterest} />)}
        </div>
      )}
    </PoPShell>
  );
};

export default PoPGaps;
